// crowd.ts — animated street life (pedestrians, animals, birds, cyclists,
// taxis). Every animated kind is one InstancedMesh: one draw call per kind,
// skeletal-style motion done in the vertex shader from two geometry
// attributes (aPart = which limb, aPivot = its joint) and per-instance
// attributes (aPhase = gait/wheel phase, aAmp = gait strength, aTint = shirt).
//
// Gameplay keeps using plain Object3D "roots" (position, rotation, physics);
// the crowd copies their matrices into the instanced mesh every frame.
import * as THREE from "three";

/** Limb ids written into the aPart geometry attribute. */
export const Part = {
  Static: 0,
  LegA: 1,
  LegB: 2,
  ArmA: 3,
  ArmB: 4,
  Wheel: 5,
  WingL: 6,
  WingR: 7,
  Tail: 8,
} as const;

export interface CrowdKindOptions {
  /** Body bob height while walking (world units). */
  bob: number;
  /** Wheel angle per gait radian (stride / (2π · wheelRadius)); 0 = no wheels. */
  wheelK: number;
}

const ANIM_HEAD = `
attribute float aPart;
attribute vec3 aPivot;
attribute float aTintMask;
attribute float aPhase;
attribute float aAmp;
attribute vec3 aTint;
uniform float uTime;
uniform float uBob;
uniform float uWheelK;
varying float vDLWorldY;

vec3 dlRot(vec3 v, int axis, float a) {
  float c = cos(a), s = sin(a);
  if (axis == 0) return vec3(v.x, c * v.y - s * v.z, s * v.y + c * v.z);
  if (axis == 1) return vec3(c * v.x + s * v.z, v.y, -s * v.x + c * v.z);
  return vec3(c * v.x - s * v.y, s * v.x + c * v.y, v.z);
}

void dlAnim(out int axis, out float ang) {
  int p = int(aPart + 0.5);
  axis = 2;
  ang = 0.0;
  float ph = aPhase;
  if (p == 1) ang = sin(ph) * 0.7 * aAmp;
  else if (p == 2) ang = -sin(ph) * 0.7 * aAmp;
  else if (p == 3) ang = -sin(ph) * 0.6 * aAmp;
  else if (p == 4) ang = sin(ph) * 0.6 * aAmp;
  else if (p == 5) ang = -ph * uWheelK;
  else if (p == 6) { axis = 0; ang = sin(uTime * 15.0 + ph) * 0.95; }
  else if (p == 7) { axis = 0; ang = -sin(uTime * 15.0 + ph) * 0.95; }
  else if (p == 8) { axis = 1; ang = sin(uTime * 9.0 + ph) * 0.55; }
}
`;

/** Shared darkening of everything that falls below street level. */
export function applyDepthFade(material: THREE.Material, animated = false, uniforms?: Record<string, THREE.IUniform>) {
  material.onBeforeCompile = (shader) => {
    if (uniforms) Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = (animated ? ANIM_HEAD : "varying float vDLWorldY;\n") + shader.vertexShader;
    if (animated) {
      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <beginnormal_vertex>",
          `#include <beginnormal_vertex>
int dlAxis; float dlAng;
dlAnim(dlAxis, dlAng);
objectNormal = dlRot(objectNormal, dlAxis, dlAng);`,
        )
        .replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>
transformed = dlRot(transformed - aPivot, dlAxis, dlAng) + aPivot;
transformed.y += abs(sin(aPhase)) * uBob * aAmp;`,
        )
        .replace(
          "#include <color_vertex>",
          `#include <color_vertex>
#ifdef USE_COLOR
vColor.rgb = mix(vColor.rgb, vColor.rgb * aTint, aTintMask);
#endif`,
        );
    }
    shader.vertexShader = shader.vertexShader.replace(
      "#include <project_vertex>",
      `#include <project_vertex>
#ifdef USE_INSTANCING
vDLWorldY = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).y;
#else
vDLWorldY = (modelMatrix * vec4(transformed, 1.0)).y;
#endif`,
    );
    shader.fragmentShader = `varying float vDLWorldY;\n${shader.fragmentShader}`.replace(
      "#include <dithering_fragment>",
      `#include <dithering_fragment>
if (vDLWorldY < 0.0) { float dl = clamp(1.0 + vDLWorldY / 7.0, 0.0, 1.0); gl_FragColor.rgb *= dl * dl; }`,
    );
  };
  material.customProgramCacheKey = () => `dl-depthfade-${animated ? "anim" : "static"}`;
}

interface Entry {
  root: THREE.Object3D;
  tint: THREE.Color;
}

class CrowdKind {
  readonly mesh: THREE.InstancedMesh;
  readonly entries: Entry[] = [];
  private readonly phase: THREE.InstancedBufferAttribute;
  private readonly amp: THREE.InstancedBufferAttribute;
  private readonly tint: THREE.InstancedBufferAttribute;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, capacity: number) {
    const g = geometry.clone();
    this.phase = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1).setUsage(THREE.DynamicDrawUsage);
    this.amp = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1).setUsage(THREE.DynamicDrawUsage);
    this.tint = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
    g.setAttribute("aPhase", this.phase);
    g.setAttribute("aAmp", this.amp);
    g.setAttribute("aTint", this.tint);
    this.mesh = new THREE.InstancedMesh(g, material, capacity);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    // Only the compacted visible set is drawn, including the shadow pass.
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = false;
  }

  get capacity() {
    return this.phase.count;
  }

  add(root: THREE.Object3D, tint: THREE.Color) {
    const index = this.entries.length;
    if (index >= this.capacity) return false;
    this.entries.push({ root, tint: tint.clone() });
    this.tint.setXYZ(index, tint.r, tint.g, tint.b);
    this.tint.needsUpdate = true;
    return true;
  }

  update() {
    // Drop roots that left the scene (respawned / disposed items).
    for (let i = this.entries.length - 1; i >= 0; i--) {
      if (this.entries[i]!.root.parent) continue;
      const last = this.entries.length - 1;
      if (i !== last) this.entries[i] = this.entries[last]!;
      this.entries.pop();
    }
    // Pack the visible roots into the front of the buffer and draw only those.
    // Hidden pedestrians then skip both the colour pass and the shadow pass.
    let visible = 0;
    for (let i = 0; i < this.entries.length; i++) {
      const entry = this.entries[i]!;
      const root = entry.root;
      if (!root.visible) continue;
      root.updateMatrix();
      this.mesh.setMatrixAt(visible, root.matrix);
      this.phase.setX(visible, (root.userData.phase as number) ?? 0);
      this.amp.setX(visible, (root.userData.amp as number) ?? 0);
      this.tint.setXYZ(visible, entry.tint.r, entry.tint.g, entry.tint.b);
      visible++;
    }
    this.mesh.count = visible;
    const dirty = visible > 0;
    this.mesh.instanceMatrix.needsUpdate = dirty;
    this.phase.needsUpdate = dirty;
    this.amp.needsUpdate = dirty;
    this.tint.needsUpdate = dirty;
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.mesh.dispose();
  }
}

export class Crowd {
  readonly group = new THREE.Group();
  private readonly kinds = new Map<number, CrowdKind>();
  private readonly time = { value: 0 };
  private readonly materials: THREE.Material[] = [];

  /** Registers a root for an animated kind; builds the kind on first use. */
  add(kind: number, geometry: () => THREE.BufferGeometry, options: CrowdKindOptions, root: THREE.Object3D, tint: THREE.Color) {
    let entry = this.kinds.get(kind);
    if (!entry || !entry.add(root, tint)) {
      // First use, or full: (re)build with more room.
      const capacity = Math.max(160, (entry?.capacity ?? 0) * 2);
      const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.02 });
      applyDepthFade(material, true, {
        uTime: this.time,
        uBob: { value: options.bob },
        uWheelK: { value: options.wheelK },
      });
      this.materials.push(material);
      const next = new CrowdKind(geometry(), material, capacity);
      if (entry) {
        // carry the old roots over
        for (const old of entry.entries) next.add(old.root, old.tint);
        this.group.remove(entry.mesh);
        entry.dispose();
      }
      this.kinds.set(kind, next);
      this.group.add(next.mesh);
      next.add(root, tint);
      entry = next;
    }
  }

  update(dt: number) {
    this.time.value += dt;
    for (const kind of this.kinds.values()) kind.update();
  }

  dispose() {
    for (const kind of this.kinds.values()) kind.dispose();
    for (const material of this.materials) material.dispose();
  }
}
